import "./scrollable.scss";

import { debounce, throttle } from "lodash";
import PropTypes from "prop-types";
import React, { PureComponent } from "react";

import Dimensions from "../../constants/styles/dimensions";
import timeoutRates from "../../constants/timeoutRates";
import { PropTypeShapes } from "../../types";
import hasAncestorOfClass from "../../utils/hasAncestorOfClass";
import {
  getEventIsTouch,
  getPointerPositionInDOMElement,
  getScreenPointerPositionFromEvent
} from "../../utils/mouseUtils";

const MINIMUM_THUMB_SIZE = 16;

const clickWasThumb = e => hasAncestorOfClass(e.target, "scrollableThumb");

const barHasMoved = (oldViewport, newViewport, horizontal) => {
  if (horizontal) {
    return !oldViewport || oldViewport.left !== newViewport.left || oldViewport.width !== newViewport.width;
  }
  return !oldViewport || oldViewport.top !== newViewport.top || oldViewport.height !== newViewport.height;
};

const getTransformValue = transformString => {
  const match = transformString.match(/\d+\.?\d*/g);
  return match ? parseFloat(match) : 0;
};

/**
 * If clicking on the bottom/right of the thumb
 * should adjust the clicking point to the expected start point
 * of the thumb
 * @param {boolean} horizontal
 * @param {object} thumbNode
 * @param {object} clickPosition
 * @returns
 */
const getScrollOffsetByThumbPosition = (horizontal, thumbNode, clickPosition) => {
  const thumbRect = thumbNode.getBoundingClientRect();
  const thumbTransformValue = getTransformValue(thumbNode.style.transform);

  const { x, y } = clickPosition;
  const { width, height } = thumbRect;
  const considerPoint = horizontal ? x : y;
  const considerSize = horizontal ? width : height;
  return considerPoint < thumbTransformValue
    ? 0
    : considerPoint > thumbTransformValue + considerSize
      ? -considerSize
      : 0;
};

export default class ScrollBar extends PureComponent {
  constructor(props, context) {
    super(props, context);
    this.state = {
      viewportRect: null,
      isDragging: false,
      isActive: false
    };
    this._isMounted = false;
    this._lastViewportRect = null;

    this.hideScrollbar = this.hideScrollbar.bind(this);
    this.debouncedHideScrollbar = debounce(this.hideScrollbar, timeoutRates.HIDE_SCROLLBAR_TIMEOUT);

    this.onTrackClick = this.onTrackClick.bind(this);
    this.onThumbMouseDown = this.onThumbMouseDown.bind(this);
    this.onThumbMouseMove = this.onThumbMouseMove.bind(this);
    this.onThumbMouseUp = this.onThumbMouseUp.bind(this);
    this.onMouseEnter = this.onMouseEnter.bind(this);
    this.onMouseLeave = this.onMouseLeave.bind(this);
    this.updateViewportRect = this.updateViewportRect.bind(this);

    // Throttled versions of functions
    this.throttledThumbMove = throttle(this.onThumbMouseMove, timeoutRates.SCROLL_THROTTLE, {
      leading: false,
      trailing: true
    });
  }

  componentWillMount() {
    this._lastViewportRect = this.props.viewportRect;
    this.setState({ viewportRect: this.props.viewportRect });
  }

  componentDidMount() {
    this._isMounted = true;
    this.props.registerUpdateFunctionWithScrollableView(this.updateViewportRect);
    // And fade the scrollbar after render if it is not required yet
    if (!this.state.mouseOver) {
      this.debouncedHideScrollbar();
    }
  }

  componentWillReceiveProps(nextProps) {
    if (this._isMounted) {
      // If it doesn't match either our current state, or our last viewport, then call setState and let's get this thing rendering again!
      if (
        barHasMoved(this.state.viewportRect, nextProps.viewportRect, this.props.horizontal) ||
        barHasMoved(this._lastViewportRect, nextProps.viewportRect, this.props.horizontal)
      ) {
        this._lastViewportRect = nextProps.viewportRect;
        this.setState({ viewportRect: nextProps.viewportRect, isActive: true });
        this.debouncedHideScrollbar();
      }
    } else {
      // eslint-disable-next-line no-console
      console.log(`ScrollBar: Caught setState call after componentWillUnmount() inside componentWillReceiveProps()`);
    }
  }

  componentDidUpdate() {
    this.updateViewportRect(this.state.viewportRect);
  }

  componentWillUnmount() {
    this._isMounted = false;
    this.throttledThumbMove.cancel();
    this.props.registerUpdateFunctionWithScrollableView(null);
    this.debouncedHideScrollbar.cancel();
  }

  updateViewportRect(viewportRect) {
    if (this._isMounted) {
      if (this._thumbNode && barHasMoved(this._lastViewportRect, viewportRect, this.props.horizontal)) {
        this._thumbNode.style.transform = this.getThumbTransformForViewport(viewportRect);
        this._lastViewportRect = viewportRect;
        if (!this.state.isActive) {
          this.setState({ viewportRect: this._lastViewportRect, isActive: true });
        }
      }
    } else {
      // eslint-disable-next-line no-console
      console.log(
        `ScrollBar: [${this._scrollbarId}] Caught attempted access to node when component is not mounted... after componentWillUnmount() inside updateViewportRect()`
      );
    }
  }

  onTrackClick(e) {
    // Ignore clicks on the thumb itself.
    if (clickWasThumb(e)) {
      return;
    }

    const mousePosInNode = getPointerPositionInDOMElement(e, this._domNode);

    const { viewRect, containerRect, horizontal } = this.props;

    // Get the dimensions where the click occurred
    let pixel = null;
    let viewSize = null;
    let trackSize = null;
    if (horizontal) {
      pixel = mousePosInNode.x;
      viewSize = viewRect.width;
      trackSize = containerRect.width;
    } else {
      pixel = mousePosInNode.y;
      viewSize = viewRect.height;
      trackSize = containerRect.height;
    }
    pixel += getScrollOffsetByThumbPosition(horizontal, this._thumbNode, mousePosInNode);
    // Now calculate a value and send
    const value = (pixel / trackSize) * viewSize + (horizontal ? viewRect.left : viewRect.top);
    this.props.onScroll(value);
  }

  onThumbMouseDown(e) {
    if (getEventIsTouch(e)) {
      document.addEventListener("touchmove", this.throttledThumbMove);
      document.addEventListener("touchend", this.onThumbMouseUp);
    } else {
      document.addEventListener("mousemove", this.throttledThumbMove);
      document.addEventListener("mouseup", this.onThumbMouseUp);
    }

    const mousePos = getScreenPointerPositionFromEvent(e);

    if (this.props.horizontal) {
      this._mouseDownPixel = mousePos.x;
      this._mouseDownViewportPosition = this.state.viewportRect.left;
      this._mouseMoveScale = this.props.viewRect.width / this.props.containerRect.width;
    } else {
      this._mouseDownPixel = mousePos.y;
      this._mouseDownViewportPosition = this.state.viewportRect.top;
      this._mouseMoveScale = this.props.viewRect.height / this.props.containerRect.height;
    }

    if (this._isMounted) {
      this.setState({ isDragging: true, viewportRect: this._lastViewportRect });
    } else {
      // eslint-disable-next-line no-console
      console.log(`ScrollBar: Caught setState call after componentWillUnmount() inside onThumbMouseDown()`);
    }
  }

  onThumbMouseMove(e) {
    const mousePos = getScreenPointerPositionFromEvent(e);

    const pixel = this.props.horizontal ? mousePos.x : mousePos.y;
    const dist = pixel - this._mouseDownPixel;
    const scrollDelta = dist * this._mouseMoveScale;
    const finalValue = this._mouseDownViewportPosition + scrollDelta;
    this.props.onScroll(finalValue);
  }

  onThumbMouseUp(_e) {
    document.removeEventListener("mousemove", this.throttledThumbMove);
    document.removeEventListener("mouseup", this.onThumbMouseUp);
    document.removeEventListener("touchmove", this.throttledThumbMove);
    document.removeEventListener("touchend", this.onThumbMouseUp);

    if (this._isMounted) {
      this.setState({
        isDragging: false,
        viewportRect: this._lastViewportRect
      });
    } else {
      // eslint-disable-next-line no-console
      console.log(`ScrollBar: Caught setState call after componentWillUnmount() inside onThumbMouseUp()`);
    }
  }

  onMouseEnter() {
    this.debouncedHideScrollbar.cancel();
    if (this._isMounted) {
      this.setState({
        isActive: true,
        mouseOver: true,
        viewportRect: this._lastViewportRect
      });
    } else {
      // eslint-disable-next-line no-console
      console.log(`ScrollBar: Caught setState call after componentWillUnmount() inside onMouseEnter()`);
    }
  }

  onMouseLeave() {
    if (this._isMounted) {
      this.setState({ mouseOver: false, viewportRect: this._lastViewportRect });
    } else {
      // eslint-disable-next-line no-console
      console.log(`ScrollBar: Caught setState call after componentWillUnmount() inside onMouseLeave()`);
    }
  }

  hideScrollbar() {
    if (this._isMounted) {
      this.setState({ isActive: false, viewportRect: this._lastViewportRect });
    }
  }

  getThumbTransformForViewport(viewportRect) {
    const sizeKey = this.props.horizontal ? "width" : "height";
    const positionKey = this.props.horizontal ? "left" : "top";
    const translateKey = this.props.horizontal ? "translateX" : "translateY";

    const viewSize = this.props.viewRect.get(sizeKey);
    const trackSize = this.props.containerRect.get(sizeKey);

    const maxOffset = 1.0 - MINIMUM_THUMB_SIZE / trackSize;
    const viewStart = this.props.viewRect.get(positionKey);
    const viewportStart = viewportRect.get(positionKey);

    const offsetPercent = Math.max(0, Math.min(maxOffset, (viewportStart - viewStart) / viewSize));
    const nodePosition = Math.round(trackSize * offsetPercent);

    return `${translateKey}(${nodePosition}px)`;
  }

  render() {
    // Nothing set yet
    if (!this.props.viewRect) {
      return null;
    }
    const sizeKey = this.props.horizontal ? "width" : "height";

    const viewSize = this.props.viewRect.get(sizeKey);
    const viewportSize = this.state.viewportRect.get(sizeKey);
    const trackSize = this.props.containerRect.get(sizeKey);

    // No need for a scrollbar
    if (viewportSize >= viewSize || this.state.viewportRect.get("height") <= Dimensions.WORKVIEW_HEADER_HEIGHT) {
      return null;
    }

    const percent = viewportSize / viewSize;
    const nodeSize = Math.round(Math.max(MINIMUM_THUMB_SIZE, trackSize * percent));
    let thumbStyle = {
      transform: this.getThumbTransformForViewport(this.state.viewportRect)
    };
    thumbStyle[sizeKey] = `${nodeSize}px`;

    const className = `scrollableTrack ${this.props.horizontal ? "horizontal" : "vertical"} ${
      this.state.isDragging ? "isDragging" : ""
    } ${this.state.isActive ? "" : "inactive"}`;

    let left = this.props.containerRect.left;
    if (!this.props.horizontal) {
      left = this.props.containerRect.right - Dimensions.SCROLLBAR_TRACK_SIZE;
      if (this.props.paddingRight) {
        left -= this.props.paddingRight;
      }
    }

    const trackStyle = this.props.horizontal
      ? {
        left: `${left}px`,
        width: `${this.props.containerRect.width}px`,
        top: `${this.props.containerRect.bottom - Dimensions.SCROLLBAR_TRACK_SIZE}px`,
        height: `${Dimensions.SCROLLBAR_TRACK_SIZE}px`
      }
      : {
        left: `${left}px`,
        width: `${Dimensions.SCROLLBAR_TRACK_SIZE}px`,
        top: `${this.props.containerRect.top}px`,
        height: `${this.props.containerRect.height}px`
      };

    return (
      <div
        className={className}
        style={trackStyle}
        onClick={this.onTrackClick}
        onMouseEnter={this.onMouseEnter}
        onMouseLeave={this.onMouseLeave}
        ref={node => (this._domNode = node)}
      >
        <div
          className="scrollableThumb"
          style={thumbStyle}
          ref={node => (this._thumbNode = node)}
          onMouseDown={this.onThumbMouseDown}
          onTouchStart={this.onThumbMouseDown}
        />
      </div>
    );
  }
}

ScrollBar.propTypes = {
  horizontal: PropTypes.bool.isRequired,
  containerRect: PropTypeShapes.flatRect.isRequired,
  paddingLeft: PropTypes.number,
  paddingRight: PropTypes.number,
  viewRect: PropTypeShapes.flatRect,
  viewportRect: PropTypeShapes.flatRect,
  registerUpdateFunctionWithScrollableView: PropTypes.func.isRequired,
  onScroll: PropTypes.func.isRequired
};
